import React, { useEffect, useState } from 'react';
import { Button, Grid, makeStyles } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { useDispatch, useSelector } from 'react-redux';
import _isEmpty from 'lodash/isEmpty';
import Modal from '../common/Modal';
import Steps from '../common/Steps';
import { TopicForm } from './TopicForm';
import { ErrorToast, MultipleErrorToast, notifySuccess } from '../utils/Utils';
import {
  setSelectedTopic,
  setSelectedTopicInfo,
  setTopicsSidebar,
  showProgress,
} from '../../redux/actions/DealershipActions';
import Service from '../../apis/service';
import { CheckinWrapper, firstCheck, validateCheckin } from '../common/CheckinWrapper';
import { apiEndpoints } from '../../constants/apiEndpoints';

const useStyle = makeStyles({
  container: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
});

export default function AddTopicPopup({ isOpen, setIsOpen, isEdit, departments }) {
  const classes = useStyle();
  const dispatch = useDispatch();
  const selectedTopicInfo = useSelector((state) => state.topicsData.selectedTopicInfo);

  const [step, setStep] = useState(1);
  const [image, setImage] = useState('');
  const [thumbnailImage, setThumbnailImage] = useState('');
  const [imageData, setImageData] = useState('');
  // const [thumbnail, setThumbnail] = useState('');
  const [thumbnailData, setThumbnailData] = useState('');
  const [topicForm, setTopicForm] = useState({
    title: '',
    description: '',
    notes: '',
    departmentId: '',
  });

  const [topicId, setTopicId] = useState(null);

  const [responsePreQuestionIds, setResponsePreQuestionIds] = useState({});
  const [responsePostQuestionIds, setResponsePostQuestionIds] = useState({});
  const [preQuestions, setPreQuestions] = useState([]);
  const [postQuestions, setPostQuestions] = useState([]);
  const [isTopicCreated, setIsTopicCreated] = useState(false);

  useEffect(() => {
    if (isEdit) {
      setTopicForm({
        title: selectedTopicInfo?.title,
        goal: selectedTopicInfo?.goal,
        description: selectedTopicInfo?.description,
        notes: selectedTopicInfo?.notes,
        departmentId: selectedTopicInfo?.departmentId,
      });
      setImage(selectedTopicInfo?.certificate);
      setThumbnailImage(selectedTopicInfo?.thumbnail);
    }
  }, []);

  const fetchDataById = async () => {
    dispatch(showProgress(true));
    try {
      const responseInitial = await Service.get(
        apiEndpoints.TOPIC_CHECKIN.replace('ID', selectedTopicInfo.initialCheckinId),
      );
      const responseFinal = await Service.get(
        apiEndpoints.TOPIC_CHECKIN.replace('ID', selectedTopicInfo.finalCheckinId),
      );
      setPreQuestions(responseInitial.form.list);
      setPostQuestions(responseFinal.form.list);
      storeIds(responseInitial, responseFinal);
      dispatch(showProgress(false));
    } catch (error) {
      dispatch(showProgress(false));
      ErrorToast(error);
    }
  };

  useEffect(() => {
    if (isEdit) {
      if (selectedTopicInfo.initialCheckinId && selectedTopicInfo.finalCheckinId) {
        fetchDataById();
      }
    }
  }, [selectedTopicInfo]);

  const storeIds = (responseInitial, responseFinal) => {
    const idsPre = {};
    const idsPost = {};

    responseInitial.form.list.forEach((ques) => {
      if (ques.type !== 'slider') {
        idsPre[ques.id] = ques.options.list.map((opt) => opt.id);
      } else {
        idsPre[ques.id] = [];
      }
    });

    responseFinal.form.list.forEach((ques) => {
      if (ques.type !== 'slider') {
        idsPost[ques.id] = ques.options.list.map((opt) => opt.id);
      } else {
        idsPost[ques.id] = [];
      }
    });

    setResponsePreQuestionIds(idsPre);
    setResponsePostQuestionIds(idsPost);
  };

  const handleValidation = (type) => {
    let isCorrect = true;
    const errors = [];

    if (type === 'topic') {
      if (!topicForm.title) {
        isCorrect = false;
        errors.push('Please enter topic name.');
      }
      if (!topicForm.goal) {
        isCorrect = false;
        errors.push('Please enter topic goal.');
      }
      if (!topicForm.description) {
        isCorrect = false;
        errors.push('Please enter what’s in it for me.');
      }
      if (!topicForm.departmentId) {
        isCorrect = false;
        errors.push('Please select a department.');
      }
      if (!thumbnailData && !isEdit) {
        isCorrect = false;
        errors.push('Please upload thumbnail');
      }
    }

    if (errors.length !== 0) {
      MultipleErrorToast(errors);
    }
    return isCorrect;
  };

  const fetchSidebarList = async (flag = true) => {
    dispatch(showProgress(true));

    try {
      const res = await Service.get(apiEndpoints.TOM_TOPICS_BY_DATES);
      dispatch(setTopicsSidebar(res));
      if (!_isEmpty(res) && flag) {
        handleListItemClick(res[0]);
      }
    } catch (error) {
      ErrorToast(error);
    }
    dispatch(showProgress(false));
  };

  const handleListItemClick = async (data = {}) => {
    dispatch(showProgress(true));
    if (!_isEmpty(data)) {
      dispatch(setSelectedTopic(data));
    }
    dispatch(showProgress(false));
  };

  const fetchSelectedTopicInfo = async () => {
    dispatch(showProgress(true));

    try {
      const res = await Service.get(apiEndpoints.TOPIC_BY_ID.replace('ID', selectedTopicInfo.id));
      dispatch(setSelectedTopicInfo(res));
    } catch (error) {
      ErrorToast(error);
    }
    dispatch(showProgress(false));
  };

  const createTopic = async (data) => {
    dispatch(showProgress(true));
    try {
      const res = await Service.post(apiEndpoints.TOPIC_CREATE, data);
      setTopicId(res.id);
      notifySuccess('Topic created successfully');
      fetchSidebarList();
      setIsTopicCreated(true);
      setStep((pre) => pre + 1);
    } catch (error) {
      ErrorToast(error);
    }
    dispatch(showProgress(false));
  };

  const updateTopic = async (data) => {
    dispatch(showProgress(true));
    try {
      await Service.post(apiEndpoints.TOPIC_UPDATE.replace('ID', selectedTopicInfo.id), data);
      notifySuccess('Topic updated successfully');
      setTopicId(selectedTopicInfo.id);
      fetchSelectedTopicInfo();
      fetchSidebarList(false);
      setStep((pre) => pre + 1);
    } catch (error) {
      ErrorToast(error);
    }
    dispatch(showProgress(false));
  };

  const addCheckin = async () => {
    if (preQuestions.length > 0 && postQuestions.length > 0) {
      if (!firstCheck(preQuestions) && !firstCheck(postQuestions)) {
        dispatch(showProgress(true));
        try {
          const temp = {
            topicId,
            initalCheckinQuestions: preQuestions,
            finalCheckinQuestions: postQuestions,
          };
          await Service.post(apiEndpoints.TOPIC_CHECKIN_CREATE, temp);
          fetchSelectedTopicInfo();
          dispatch(showProgress(false));
          notifySuccess('Saved Successfully');
          setIsOpen(false);
        } catch (error) {
          ErrorToast(error);
          dispatch(showProgress(false));
        }
      } else if (firstCheck(preQuestions) && firstCheck(postQuestions)) {
        notifySuccess('Saved Successfully');
        setIsOpen(false);
        dispatch(showProgress(false));
      } else if (firstCheck(preQuestions)) {
        ErrorToast('Pre Assesments is required');
        dispatch(showProgress(false));
      } else if (firstCheck(postQuestions)) {
        ErrorToast('Post Assesments is required');
        dispatch(showProgress(false));
      }
    } else if (preQuestions.length > 0) {
      ErrorToast('Post Assesments is required');
    } else if (postQuestions.length > 0) {
      ErrorToast('Pre  Assesments is required');
    } else {
      notifySuccess('Saved Successfully');
      setIsOpen(false);
      dispatch(showProgress(false));
    }
  };

  const editCheckin = async () => {
    if (preQuestions.length > 0 && postQuestions.length > 0) {
      dispatch(showProgress(true));
      try {
        await Service.put(apiEndpoints.TOPIC_CHECKIN_UPDATE, {
          topicId: selectedTopicInfo.id,
          initalCheckinId: selectedTopicInfo.initialCheckinId,
          finalCheckinId: selectedTopicInfo.finalCheckinId,
          initalCheckinQuestions: preQuestions,
          finalCheckinQuestions: postQuestions,
        });
        dispatch(showProgress(false));
        notifySuccess('Saved Successfully');
        setIsOpen(false);
      } catch (error) {
        ErrorToast(error);
        dispatch(showProgress(false));
      }
    } else {
      notifySuccess('Saved Successfully');
      setIsOpen(false);
      dispatch(showProgress(false));
    }
  };

  const handleNext = async () => {
    if (step === 1) {
      const isValid = handleValidation('topic');
      if (!isValid) return;
      const data = new global.FormData();
      data.append('title', topicForm.title);
      data.append('goal', topicForm.goal);
      data.append('description', topicForm.description);
      data.append('departmentId', topicForm.departmentId);
      if (topicForm.notes) {
        data.append('notes', topicForm.notes);
      }
      if (imageData) {
        data.append('certificate', imageData);
      }
      if (thumbnailData) {
        data.append('thumbnail', thumbnailData);
      }
      if (isEdit || isTopicCreated) {
        updateTopic(data);
      } else {
        createTopic(data);
      }
    } else if (step === 2) {
      const isValid = validateCheckin(preQuestions);
      if (!isValid) return;
      setStep((pre) => pre + 1);
    } else if (!isEdit) {
      const isValid = validateCheckin(postQuestions);
      if (!isValid) return;
      try {
        dispatch(showProgress(true));
        await addCheckin();
        dispatch(showProgress(false));
      } catch (error) {
        dispatch(showProgress(false));
        ErrorToast(error);
      }
    } else {
      const isValid = validateCheckin(postQuestions);
      if (!isValid) return;
      // eslint-disable-next-line
      if (selectedTopicInfo.initialCheckinId && selectedTopicInfo.finalCheckinId) {
        try {
          dispatch(showProgress(true));
          await editCheckin();
          dispatch(showProgress(false));
        } catch (error) {
          dispatch(showProgress(false));
          ErrorToast(error);
        }
      } else {
        try {
          dispatch(showProgress(true));
          await addCheckin();
          dispatch(showProgress(false));
        } catch (error) {
          dispatch(showProgress(false));
          ErrorToast(error);
        }
      }
    }
  };

  return (
    <Modal isOpen={isOpen} height="85vh" width="65vw">
      <Grid className={classes.container}>
        <Grid container direction="row" justifyContent="space-between" alignItems="flex-start">
          <Grid item>
            <Grid item style={{ fontWeight: '600', fontSize: '18px' }}>
              {isEdit ? 'Edit Topic' : 'Add Topic'}
            </Grid>
          </Grid>
          <Grid item>
            <CloseIcon onClick={() => setIsOpen(false)} style={{ cursor: 'pointer' }} />
          </Grid>
        </Grid>

        <Steps step={step} stepInfo={['Topic Detail', 'Pre-Assessment', 'Post-Assessment']} />
        <Grid style={{ height: '65vh' }}>
          {step === 1 && (
            <TopicForm
              form={topicForm}
              setForm={setTopicForm}
              departments={departments}
              image={image}
              thumbnailImage={thumbnailImage}
              setImage={setImage}
              setThumbnailImage={setThumbnailImage}
              setImageData={setImageData}
              thumbnailData={thumbnailData}
              setThumbnailData={setThumbnailData}
            />
          )}
          {step === 2 && (
            <CheckinWrapper
              questionList={preQuestions}
              setQustionList={setPreQuestions}
              responseQuestionIds={responsePreQuestionIds}
            />
          )}

          {step === 3 && (
            <CheckinWrapper
              questionList={postQuestions}
              setQustionList={setPostQuestions}
              responseQuestionIds={responsePostQuestionIds}
            />
          )}
        </Grid>
        <Grid style={{ display: 'flex' }}>
          {step > 1 && (
            <Button onClick={() => setStep((pre) => pre - 1)} variant="outlined">
              Back
            </Button>
          )}
          <Grid container direction="row" justifyContent="flex-end" alignItems="center">
            <>
              <Button onClick={() => setIsOpen(false)} variant="outlined">
                Cancel
              </Button>
              <Button variant="contained" color="primary" style={{ marginLeft: '10px' }} onClick={handleNext}>
                {step !== 3 ? 'Next' : 'Save'}
              </Button>
            </>
          </Grid>
        </Grid>
      </Grid>
    </Modal>
  );
}
